import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

const ANCHOR_DURATION = 450;

/** @type {(() => void) | null} */
let stopActiveAnchor = null;

/**
 * Ease in out cubic, so the scroll starts and ends at rest instead of snapping.
 *
 * @param {number} progress - Progress between 0 and 1.
 * @returns {number}
 */
function easeInOutCubic(progress) {
  return progress < 0.5 ? 4 * progress ** 3 : 1 - (-2 * progress + 2) ** 3 / 2;
}

/**
 * Scrolls the document until the given element sits at `offset` from the top of the viewport,
 * re-measuring the destination on every frame so that layout shifts happening at the same time
 * are followed instead of fought.
 *
 * @param {HTMLElement} element - The element to anchor.
 * @param {number} offset - Distance to keep between the viewport top and the element.
 */
function anchorToViewportTop(element, offset) {
  stopActiveAnchor?.();

  const controller = new AbortController();
  const start = performance.now();
  const startScroll = window.scrollY;
  const instant = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  let frame = 0;

  const stop = () => {
    cancelAnimationFrame(frame);
    controller.abort();

    if (stopActiveAnchor === stop) {
      stopActiveAnchor = null;
    }
  };

  const step = () => {
    if (!element.isConnected) {
      stop();
      return;
    }

    const destination = window.scrollY + element.getBoundingClientRect().top - offset;
    const progress = instant ? 1 : Math.min(1, (performance.now() - start) / ANCHOR_DURATION);

    window.scrollTo({
      top: startScroll + (destination - startScroll) * easeInOutCubic(progress),
      behavior: 'instant',
    });

    if (progress < 1) {
      frame = requestAnimationFrame(step);
    } else {
      stop();
    }
  };

  stopActiveAnchor = stop;

  for (const type of ['wheel', 'touchmove', 'keydown']) {
    window.addEventListener(type, stop, { signal: controller.signal, passive: true });
  }

  frame = requestAnimationFrame(step);
}

// Accordion
// Still extends HTMLElement over Component so that refs are still available to parent components (e.g. SortingFilterComponent)
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();

  #openedByUserAt = 0;

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    this.details.addEventListener('toggle', this.#handleToggle, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }

    this.#openedByUserAt = Date.now();
    this.classList.add('is-animating');
  };

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    this.classList.remove('is-animating');

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.details.open = false;
      this.summary.focus();
    }
  }

  /**
   * @param {Event} event
   */
  #handleToggle = (event) => {
    const details = event.target;

    if (!details || !(details instanceof HTMLDetailsElement) || !details.open) {
      return;
    }

    const accordion = this.closest('.accordion');

    if (!accordion) {
      return;
    }

    accordion.querySelectorAll('accordion-custom').forEach((accordionItem) => {
      if (accordionItem === this || !(accordionItem instanceof AccordionCustom)) {
        return;
      }

      const otherDetails = accordionItem.details;

      if (otherDetails.open) {
        accordionItem.classList.add('is-animating');
        otherDetails.classList.add('is-closing');

        // Delay closing so the browser can apply the closing transition
        setTimeout(() => {
          otherDetails.open = false;
        }, 10);

        const removeClosingClass = () => {
          otherDetails.classList.remove('is-closing');
          otherDetails.removeEventListener('transitionend', removeClosingClass);
        };

        otherDetails.addEventListener('transitionend', removeClosingClass, { once: true });
        setTimeout(removeClosingClass, 500);
      }
    });

    this.#alignWithViewportTop();
  };

  /**
   * Keeps the opened accordion anchored right below the sticky header while the siblings
   * above it collapse, so the content never jumps out of the viewport.
   */
  #alignWithViewportTop() {
    if (Date.now() - this.#openedByUserAt > 1000) {
      return;
    }

    this.#openedByUserAt = 0;

    if (this.#hasScrollableAncestor()) {
      return;
    }

    const offset = parseFloat(getComputedStyle(this).scrollMarginBlockStart) || 0;

    anchorToViewportTop(this, offset);
  }

  /**
   * Checks whether the accordion lives inside a dialog, drawer or any other scrollable container,
   * where scrolling the document would be wrong.
   *
   * @returns {boolean}
   */
  #hasScrollableAncestor() {
    let element = this.parentElement;

    while (element && element !== document.body) {
      if (element instanceof HTMLDialogElement || element.hasAttribute('popover')) {
        return true;
      }

      const styles = getComputedStyle(element);

      if (styles.position === 'fixed') {
        return true;
      }

      if (/(auto|scroll)/.test(styles.overflowY) && element.scrollHeight > element.clientHeight + 1) {
        return true;
      }

      element = element.parentElement;
    }

    return false;
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
